// analytics/components/charts/TrendChart.jsx - Enhanced with comprehensive debugging

import { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';
import 'chartjs-adapter-date-fns'; 

const chartInstances = {};

export const TrendChart = ({ chartId, data, title }) => {
    const chartRef = useRef(null);

    useEffect(() => {
        console.log(`[TrendChart-${chartId}] Starting render with data:`, {
            dataLength: data?.length,
            dataPreview: data?.slice(0, 3),
            chartId,
            title
        });

        if (!chartRef.current || !data || data.length === 0) {
            console.log(`[TrendChart-${chartId}] No data or ref, cleaning up`);
            if (chartInstances[chartId]) {
                chartInstances[chartId].destroy();
                delete chartInstances[chartId];
            }
            return;
        }

        // Destroy previous chart instance
        if (chartInstances[chartId]) {
            console.log(`[TrendChart-${chartId}] Destroying previous chart instance`);
            chartInstances[chartId].destroy();
        }

        const ctx = chartRef.current.getContext('2d');
        
        // Enhanced data processing with detailed logging
        console.log(`[TrendChart-${chartId}] Raw data timestamps:`, data.map(d => ({
            timestamp: d.timestamp,
            type: typeof d.timestamp,
            isValidDate: !isNaN(new Date(d.timestamp)),
            parsedDate: new Date(d.timestamp).toISOString()
        })));

        const processedData = data.map((d, index) => {
            const timestamp = d.timestamp;
            let parsedDate;
            
            // Handle different timestamp formats
            if (timestamp instanceof Date) {
                parsedDate = timestamp;
            } else if (typeof timestamp === 'string') {
                parsedDate = new Date(timestamp);
            } else if (typeof timestamp === 'number') {
                // Handle both seconds and milliseconds
                parsedDate = new Date(timestamp > 1e10 ? timestamp : timestamp * 1000);
            } else {
                console.error(`[TrendChart-${chartId}] Invalid timestamp at index ${index}:`, timestamp);
                parsedDate = new Date(); // Fallback to current time
            }

            // Validate the parsed date
            if (isNaN(parsedDate.getTime())) {
                console.error(`[TrendChart-${chartId}] Invalid date created from timestamp:`, timestamp);
                parsedDate = new Date(); // Fallback to current time
            }

            return {
                x: parsedDate,
                y: Number(d.defect_rate) || 0,
                totalCount: Number(d.total_count) || 0,
                defectCount: Number(d.defect_count) || 0,
                originalTimestamp: timestamp
            };
        });

        console.log(`[TrendChart-${chartId}] Processed data:`, processedData.map(d => ({
            x: d.x.toISOString(),
            y: d.y,
            totalCount: d.totalCount,
            originalTimestamp: d.originalTimestamp
        })));

        const totalCountData = processedData.map(d => ({
            x: d.x,
            y: d.totalCount
        }));

        // Enhanced time span calculation with safety checks
        let timeSpan = 0;
        let spanHours = 0;
        
        if (processedData.length > 1) {
            const firstTime = processedData[0].x.getTime();
            const lastTime = processedData[processedData.length - 1].x.getTime();
            
            console.log(`[TrendChart-${chartId}] Time range:`, {
                first: processedData[0].x.toISOString(),
                last: processedData[processedData.length - 1].x.toISOString(),
                firstTime,
                lastTime,
                difference: lastTime - firstTime
            });
            
            if (!isNaN(firstTime) && !isNaN(lastTime)) {
                timeSpan = Math.abs(lastTime - firstTime);
                spanHours = timeSpan / (1000 * 60 * 60);
            } else {
                console.error(`[TrendChart-${chartId}] Invalid time calculations`, { firstTime, lastTime });
            }
        } else if (processedData.length === 1) {
            console.log(`[TrendChart-${chartId}] Single data point detected`);
            // For single points, create a small artificial span
            spanHours = 2; // 2 hours for better visualization
        }
        
        console.log(`[TrendChart-${chartId}] Time span calculation:`, {
            timeSpan,
            spanHours,
            dataPoints: processedData.length
        });

        // Determine time unit with fallbacks
        let timeUnit, stepSize, tooltipFormat, displayFormat;
        
        if (spanHours <= 0.1 || processedData.length === 1) {
            // Very short span or single point
            timeUnit = 'minute';
            stepSize = 5;
            tooltipFormat = 'MMM d, HH:mm:ss';
            displayFormat = 'HH:mm';
        } else if (spanHours <= 2) {
            timeUnit = 'minute';
            stepSize = 10;
            tooltipFormat = 'MMM d, HH:mm';
            displayFormat = 'HH:mm';
        } else if (spanHours <= 24) {
            timeUnit = 'hour';
            stepSize = Math.max(1, Math.floor(spanHours / 12));
            tooltipFormat = 'MMM d, HH:mm';
            displayFormat = 'HH:mm';
        } else if (spanHours <= 168) { // 1 week
            timeUnit = 'day';
            stepSize = 1;
            tooltipFormat = 'MMM d, yyyy';
            displayFormat = 'MMM d';
        } else {
            timeUnit = 'day';
            stepSize = Math.max(1, Math.ceil(spanHours / (24 * 7))); // Weekly steps
            tooltipFormat = 'MMM d, yyyy';
            displayFormat = 'MMM d';
        }

        console.log(`[TrendChart-${chartId}] Chart configuration:`, {
            timeUnit,
            stepSize,
            tooltipFormat,
            displayFormat,
            dataLength: data.length
        });

        // Calculate axis bounds for better visualization
        let xAxisMin, xAxisMax;
        if (processedData.length === 1) {
            const centerTime = processedData[0].x.getTime();
            xAxisMin = new Date(centerTime - (2 * 60 * 60 * 1000)); // 2 hours before
            xAxisMax = new Date(centerTime + (2 * 60 * 60 * 1000)); // 2 hours after
        } else if (processedData.length > 1) {
            // Add 5% padding on each side
            const firstTime = processedData[0].x.getTime();
            const lastTime = processedData[processedData.length - 1].x.getTime();
            const padding = Math.max(timeSpan * 0.05, 1000 * 60 * 5); // At least 5 minutes
            xAxisMin = new Date(firstTime - padding);
            xAxisMax = new Date(lastTime + padding);
        }

        console.log(`[TrendChart-${chartId}] Axis bounds:`, {
            xAxisMin: xAxisMin?.toISOString(),
            xAxisMax: xAxisMax?.toISOString()
        });

        const config = {
            type: 'line',
            data: {
                datasets: [
                    {
                        label: 'Defect Rate (%)',
                        data: processedData,
                        borderColor: '#dc3545',
                        backgroundColor: 'rgba(220, 53, 69, 0.1)',
                        yAxisID: 'y',
                        tension: processedData.length === 1 ? 0 : 0.2,
                        fill: true,
                        pointRadius: processedData.length === 1 ? 8 : 4,
                        pointHoverRadius: processedData.length === 1 ? 10 : 6,
                        pointBackgroundColor: '#dc3545',
                        pointBorderColor: '#fff',
                        pointBorderWidth: 2,
                        showLine: true,
                    },
                    {
                        label: 'Total Inspections',
                        data: totalCountData,
                        borderColor: '#007bff',
                        backgroundColor: 'rgba(0, 123, 255, 0.1)',
                        yAxisID: 'y1',
                        tension: processedData.length === 1 ? 0 : 0.2,
                        pointRadius: processedData.length === 1 ? 6 : 3,
                        pointHoverRadius: processedData.length === 1 ? 8 : 5,
                        pointBackgroundColor: '#007bff',
                        pointBorderColor: '#fff',
                        pointBorderWidth: 2,
                        showLine: true,
                    }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                    intersect: false,
                    mode: 'index',
                },
                plugins: {
                    title: {
                        display: !!title,
                        text: title,
                        font: { size: 16 }
                    },
                    tooltip: {
                        callbacks: {
                            title: (tooltipItems) => {
                                try {
                                    const date = new Date(tooltipItems[0].parsed.x);
                                    return date.toLocaleString();
                                } catch (error) {
                                    console.error(`[TrendChart-${chartId}] Tooltip error:`, error);
                                    return 'Invalid Date';
                                }
                            },
                            footer: (tooltipItems) => {
                                try {
                                    const dataIndex = tooltipItems[0].dataIndex;
                                    const point = data[dataIndex];
                                    if (point) {
                                        return `Total: ${point.total_count}\nDefects: ${point.defect_count}`;
                                    }
                                    return '';
                                } catch (error) {
                                    console.error(`[TrendChart-${chartId}] Tooltip footer error:`, error);
                                    return '';
                                }
                            }
                        }
                    },
                    legend: {
                        display: true,
                        position: 'top'
                    }
                },
                scales: {
                    x: {
                        type: 'time',
                        time: {
                            unit: timeUnit,
                            stepSize: stepSize,
                            tooltipFormat: tooltipFormat,
                            displayFormats: {
                                minute: displayFormat,
                                hour: displayFormat,
                                day: displayFormat
                            }
                        },
                        title: {
                            display: true,
                            text: 'Time'
                        },
                        grid: {
                            display: true,
                            color: 'rgba(0,0,0,0.1)'
                        },
                        min: xAxisMin,
                        max: xAxisMax,
                    },
                    y: {
                        type: 'linear',
                        position: 'left',
                        title: {
                            display: true,
                            text: 'Defect Rate (%)'
                        },
                        beginAtZero: true,
                        max: processedData.length === 1 && processedData[0].y === 0 ? 10 : undefined,
                        grid: {
                            color: 'rgba(220, 53, 69, 0.1)'
                        }
                    },
                    y1: {
                        type: 'linear',
                        position: 'right',
                        title: {
                            display: true,
                            text: 'Total Inspections'
                        },
                        beginAtZero: true,
                        grid: {
                            drawOnChartArea: false,
                        },
                        max: processedData.length === 1 ? Math.max(10, totalCountData[0].y * 1.2) : undefined,
                    }
                },
                animation: {
                    duration: processedData.length === 1 ? 1000 : 750,
                    easing: 'easeInOutQuart'
                }
            }
        };

        console.log(`[TrendChart-${chartId}] Creating chart with config:`, {
            datasets: config.data.datasets.length,
            xScale: config.options.scales.x.time,
            hasValidData: processedData.every(d => !isNaN(d.x.getTime()))
        });

        try {
            chartInstances[chartId] = new Chart(ctx, config);
            console.log(`[TrendChart-${chartId}] Chart created successfully`);
        } catch (error) {
            console.error(`[TrendChart-${chartId}] Chart creation failed:`, error);
            // Show error in the chart area
            ctx.fillStyle = '#dc3545';
            ctx.font = '16px Arial';
            ctx.textAlign = 'center';
            ctx.fillText('Chart Error: ' + error.message, ctx.canvas.width / 2, ctx.canvas.height / 2);
        }

        // Cleanup function
        return () => {
            if (chartInstances[chartId]) {
                console.log(`[TrendChart-${chartId}] Cleaning up chart instance`);
                chartInstances[chartId].destroy();
                delete chartInstances[chartId];
            }
        };
    }, [chartId, data, title]);

    // Enhanced fallback messages
    if (!data || data.length === 0) {
        return (
            <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                <div className="text-center">
                    <i className="fas fa-chart-line fa-3x mb-3"></i>
                    <h6>No trend data available</h6>
                    <p className="small">Data points will appear as inspections are processed over time</p>
                    <small className="text-muted">Chart ID: {chartId}</small>
                </div>
            </div>
        );
    }

    if (data.length === 1) {
        return (
            <div className="position-relative h-100">
                <canvas ref={chartRef} style={{height: '100%', width: '100%'}}></canvas>
                {/* <div className="position-absolute top-0 end-0 p-2">
                    <small className="badge bg-info">
                        <i className="fas fa-info-circle me-1"></i>
                        Single data point
                    </small>
                </div> */}
                <div className="position-absolute bottom-0 start-0 p-1">
                    <small className="text-muted" style={{fontSize: '10px'}}>
                        Chart: {chartId}
                    </small>
                </div>
            </div>
        );
    }

    return (
        <div className="position-relative h-100">
            <canvas ref={chartRef} style={{height: '100%', width: '100%'}}></canvas>
            <div className="position-absolute bottom-0 start-0 p-1">
                <small className="text-muted" style={{fontSize: '10px'}}>
                    Chart: {chartId} | Points: {data.length}
                </small>
            </div>
        </div>
    );
};